import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFlexLayout, useTable } from 'react-table';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  WindowScroller,
} from 'react-virtualized';
import { injectIntl } from 'react-intl';
import classnames from 'classnames';

import intlShape from '@/shared/utils/intlShape';
import { defaultColumn, getColumns } from './helpers';

const getStyles = (props, align = 'left') => [
  props,
  {
    style: {
      textAlign: align,
    },
  },
];
const headerProps = (props, { column }) => getStyles(props, column.align);
const headerGroupProps = props => getStyles(props, 'center');

const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const cache = new CellMeasurerCache({
  defaultHeight: 37,
  minHeight: 37,
  fixedWidth: true,
});

const RegistrationsPerReportTable = ({ data, showDetails, intl, lastGroup, totals }) => {
  const columns = useMemo(() => getColumns(intl, showDetails, totals), [showDetails, intl, totals]);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      defaultColumn,
    },
    useFlexLayout,
  );

  const RenderRow = React.useCallback(
    ({ index, style, parent, key }) => {
      const row = rows[index];
      prepareRow(row);
      const { rowType, rowDepth } = row.original;
      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
          <div
            {...row.getRowProps({
              style,
              className: classnames('tr', `indent-${rowDepth}`, {
                totals: rowType === -1,
                details: lastGroup === rowType,
              }),
            })}
          >
            {row.cells.map(cell => (
              <div {...cell.getCellProps(cellProps)} className="td">
                {cell.render('Cell')}
              </div>
            ))}
          </div>
        </CellMeasurer>
      );
    },
    [prepareRow, rows, lastGroup],
  );

  useEffect(() => {
    // Clear cached row heights when data changes
    cache.clearAll();
  }, [data]);

  const handleResize = useCallback(() => {
    // Clear cached row heights when window resizes
    cache.clearAll();
  }, []);

  return (
    <div
      {...getTableProps(props => [
        props,
        { className: 'table registrations-per-report-table table-report grouped-report' },
      ])}
    >
      <div className="thead">
        {headerGroups.map(headerGroup => (
          <div {...headerGroup.getHeaderGroupProps(headerGroupProps)} className="tr">
            {headerGroup.headers.map(column => (
              <div {...column.getHeaderProps(headerProps)} className="th">
                {column.render('Header')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <WindowScroller>
        {({ height, isScrolling, registerChild, scrollTop, onChildScroll }) => (
          <div {...getTableBodyProps()} className="tbody" ref={registerChild}>
            <AutoSizer disableHeight onResize={handleResize}>
              {({ width }) => (
                <List
                  autoHeight
                  height={height}
                  isScrolling={isScrolling}
                  rowCount={rows.length}
                  rowHeight={cache.rowHeight}
                  rowRenderer={RenderRow}
                  onScroll={onChildScroll}
                  scrollTop={scrollTop}
                  width={width}
                />
              )}
            </AutoSizer>
          </div>
        )}
      </WindowScroller>
    </div>
  );
};
RegistrationsPerReportTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showDetails: PropTypes.bool.isRequired,
  lastGroup: PropTypes.number.isRequired,
  intl: intlShape.isRequired,
};
export default injectIntl(RegistrationsPerReportTable);
